import { transportChoiceToBranch } from "@japangolmok/content";
import { DeviceDataPanel } from "./trips/DeviceDataPanel";
import { AppIssueForm } from "./trips/AppIssueForm";
import { GuidePage, guideTitles } from "./guides/GuidePages";
import { SharePage } from "./trips/SharePages";
import { useEffect, useMemo, useState } from "react";
import {
	cityGuides,
	currentStaticRoutes,
	editionPhotos,
	editions,
	gatewayCoverageSource,
	isCurrentRoute,
	koreaJapanGatewayCoverage,
	officialTravelTools,
	origins,
	routeSnapshotMeta,
	routeSources,
	routes,
	routesForOrigin,
	type AirportCode,
	type Edition,
	type OriginCode,
} from "@japangolmok/content";
import { timeBucket, weekdaysFromMask } from "@japangolmok/core";
import { track } from "./analytics/events";
import { apiUrl } from "./api-url";
import {
	getCard,
	listCards,
	needsCardRefresh,
	removeCard,
	saveCard,
	serializeCard,
	type ArrivalCard,
} from "./storage/cards";
import { BrandMark } from "./ui/BrandMark";
import { ExternalLink, Layout, SourceStamp, usePageMeta } from "./ui/Shell";
import {
	TripCreatePage,
	TripDetailPage,
	TripListPage,
	TripTodayPage,
} from "./trips/TripPages";
import { WalkDetailPage, WalkListPage } from "./trips/WalkPages";
import { TripFilmPage } from "./trips/TripFilmPage";
import { TravelHome, TodayLandingPage } from "./trips/TravelHome";
import { PublicGuideAd } from "./ads/GuideEndAd";
import { CityGuideSections } from "./editions/CityGuideSections";
import {
	automationHeadline,
	automationSummary,
	customerSectionLabels,
	legalSectionLabels,
	readAutomationStatus,
	type AutomationStatus,
} from "./content/customer-language";

const destinationCodes = Object.keys(editions) as AirportCode[];
const weekdayOrder = ["월", "화", "수", "목", "금", "토", "일"];
const responsivePhoto = (path: string) =>
	`${path.replace(/\.webp$/, "-480.webp")} 480w, ${path.replace(/\.webp$/, "-960.webp")} 960w, ${path} 1280w`;
const coverageOriginCity = (origin: OriginCode) =>
	origin === "ICN" || origin === "GMP"
		? "서울"
		: origin === "PUS"
			? "부산"
			: origin === "CJJ"
				? "청주"
				: origin === "TAE"
					? "대구"
					: origin === "CJU"
						? "제주"
						: origin === "MWX"
							? "무안"
							: "양양";

type RouteService = (typeof routes)[number];

function useOfficialRoutes(origin: OriginCode, destination?: AirportCode) {
	const fallback = useMemo(
		() =>
			currentStaticRoutes().filter(
				(route) =>
					route.origin === origin &&
					(!destination || route.destination === destination),
			),
		[origin, destination],
	);
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<{
		items: RouteService[];
		checkedAt: string;
		live: boolean;
		loading: boolean;
        unavailable: boolean;
	}>({
		items: fallback,
		checkedAt: routeSnapshotMeta.checkedAt,
		live: false,
		loading: !fallback.length,
        unavailable: false,
	});
	useEffect(() => {
		const controller = new AbortController();
		const path: `/api/${string}` = destination
			? `/api/v1/routes/${origin}-${destination}`
			: `/api/v1/routes?origin=${origin}`;
		fetch(apiUrl(path), {
			headers: { accept: "application/json" },
			signal: controller.signal,
		})
			.then(async (response) =>
				response.ok
					? (response.json() as Promise<{
							items?: unknown;
							checkedAt?: unknown;
							dataState?: unknown;
						}>)
					: Promise.reject(new Error("route_api_unavailable")),
			)
			.then((payload) => {
				if (!Array.isArray(payload.items)) throw new Error("route_api_invalid");
				const checkedAt =
					typeof payload.checkedAt === "string"
						? payload.checkedAt
						: routeSnapshotMeta.checkedAt;
				const valid = payload.items
					.filter((item): item is RouteService =>
						Boolean(
							item &&
								typeof item === "object" &&
								"origin" in item &&
								"destination" in item &&
								"departureLocal" in item &&
								"weekdayMask" in item &&
								"effectiveTo" in item,
						),
					)
					.filter(
						(item) =>
							item.origin === origin &&
							(!destination || item.destination === destination) &&
							isCurrentRoute(item, checkedAt),
					);
				setState({
					items: valid,
					checkedAt,
					live: payload.dataState === "official_api",
					loading: false,
                    unavailable: false,
				});
			})
			.catch(() => { if (!controller.signal.aborted) setState((previous) => ({ ...previous, loading: false, unavailable: true })); });
		return () => controller.abort();
	}, [origin, destination, attempt]);
	return { ...state, retry: () => { setState(previous => ({...previous, loading: true, unavailable: false})); setAttempt(n => n + 1); } };
}

function HomePage() {
	usePageMeta(
		"일본골목 — 일본 여행의 시작부터 돌아온 뒤까지",
		"한국 출발공항의 일본 직항부터 일정, 준비, 예산, 도착 첫 길과 현지 오늘 모드까지 한 번에 이어 가요.",
	);
	useEffect(() => {
		const query = new URLSearchParams(window.location.search);
		const surface = query.has("utm_source")
			? "campaign"
			: document.referrer
				? document.referrer.startsWith(window.location.origin)
					? "internal"
					: "referral"
				: "direct";
		track("landing_view", { surface });
	}, []);
	return (
		<Layout>
			<section className="hero home-origin-desk paper-pattern">
				<p className="home-route-label">
					직항 확인본 · {gatewayCoverageSource.effectiveFrom}–
					{gatewayCoverageSource.effectiveTo}
				</p>
				<h1>
					어느 공항에서
					<br />
					떠나세요?
				</h1>
				<p className="hero-copy">
					출발공항을 고르면 확인한 일본 직항과 도착 뒤 첫 이동을 볼 수 있어요.
					마음에 드는 곳은 여행 계획으로 이어가세요.
				</p>
				<div className="origin-grid" aria-label="한국 출발공항">
					{origins.map((origin) => {
						const count = new Set(
							currentStaticRoutes()
								.filter((route) => route.origin === origin.iata)
								.map((route) => route.destination),
						).size;
						const regionLabel = origin.airportName.startsWith(origin.city)
							? ""
							: `${origin.city} · `;
						return (
							<a
								key={origin.iata}
								className="origin-button"
								href={`/from/${origin.iata}`}
								onClick={() =>
									track("airport_source_select", { originAirport: origin.iata })
								}
							>
								<strong>
									{origin.airportName.replace("국제공항", "공항")}
								</strong>
								<small>
									{regionLabel}
									{count
										? `확인한 일본 직항 ${count}곳`
										: "현재 운항편 확인하기"}
								</small>
								<span className="iata">{origin.iata}</span>
							</a>
						);
					})}
				</div>
				<div className="truth-note">
					<span className="truth-dot" /> 공항을 고르면 현재 확인 가능한 운항편과
					정보의 확인 시점을 보여드려요. 예약 전에는 항공사에서 다시 확인해
					주세요.
				</div>
				<div className="home-secondary-paths">
					<a className="home-return-link" href="/walks">
						날짜 없이 동네의 반나절부터 고르기 →
					</a>
					<a className="home-return-link" href="/trips">
						저장한 여행 이어보기 →
					</a>
				</div>
			</section>
		</Layout>
	);
}

function OriginPage({ origin }: { origin: OriginCode }) {
	const originInfo = origins.find((item) => item.iata === origin);
	const official = useOfficialRoutes(origin);
	const query = new URLSearchParams(location.search);
	const [dayFilter, setDayFilter] = useState(query.get("day") ?? "");
	const [timeFilter, setTimeFilter] = useState(query.get("time") ?? "");
	const services = official.items.filter(
		(s) =>
			(!dayFilter ||
				Boolean(
					s.weekdayMask &
						(1 <<
							["mon", "tue", "wed", "thu", "fri", "sat", "sun"].indexOf(
								dayFilter,
							)),
				)) &&
			(!timeFilter ||
				(timeFilter === "am"
					? Number(s.departureLocal.slice(0, 2)) < 12
					: timeFilter === "pm"
						? Number(s.departureLocal.slice(0, 2)) >= 12 &&
							Number(s.departureLocal.slice(0, 2)) < 18
						: Number(s.departureLocal.slice(0, 2)) >= 18)),
	);
	const setFilter = (day: string, time: string) => {
		setDayFilter(day);
		setTimeFilter(time);
		const q = new URLSearchParams(location.search);
		if (day) q.set("day", day);
		else q.delete("day");
		if (time) q.set("time", time);
		else q.delete("time");
		history.replaceState(null, "", location.pathname + "?" + q);
	};
	const grouped = useMemo(
		() =>
			destinationCodes
				.map((destination) => {
					const items = services.filter(
						(service) => service.destination === destination,
					);
					const mask = items.reduce(
						(total, item) => total | item.weekdayMask,
						0,
					);
					return {
						destination,
						items,
						mask,
						buckets: [
							...new Set(items.map((item) => timeBucket(item.departureLocal))),
						],
					};
				})
				.filter((group) => group.items.length > 0),
		[services],
	);
	const coverageCity = coverageOriginCity(origin);
	const coverageDestinations = useMemo(
		() =>
			koreaJapanGatewayCoverage
				.filter(([, cities]) => cities.includes(coverageCity as never))
				.map(([code]) => code as AirportCode),
		[coverageCity],
	);
	const detailedDestinations = new Set(
		grouped.map((group) => group.destination),
	);
	const coverageOnly = coverageDestinations.filter(
		(code) => !detailedDestinations.has(code),
	);
	usePageMeta(
		`${origin}에서 일본 가기 — 일본골목`,
		`${originInfo?.city ?? origin} 출발 일본 직항과 운항 요일, 도착 뒤 첫 길을 확인해요.`,
	);
	useEffect(
		() => track("route_result_view", { originAirport: origin }),
		[origin],
	);
	return (
		<Layout>
			<section className="route-head route-head--results paper-pattern">
				<a className="back-link" href="/flights">
					← 다른 출발공항
				</a>
				<span className="eyebrow">
					{originInfo?.airportName} · {origin}
				</span>
				<h1>{originInfo?.city} 출발 일본 직항</h1>
                <nav className="origin-chips" aria-label="출발공항 바꾸기">{origins.map(item => <a key={item.iata} href={`/from/${item.iata}`} aria-current={item.iata === origin ? "page" : undefined}>{item.city}</a>)}</nav>
				<p className="route-result-summary" role="status">
					{grouped.length ? (
						<>
							<strong>확인된 일본 공항 {grouped.length}곳</strong> · 공항을
							고르면 운항편과 첫 이동을 볼 수 있어요.
						</>
					) : official.loading ? (
						"현재 운항편을 확인하고 있어요."
					) : (
						"지금은 현재 편명·시간을 확인할 수 없어요. 공식 시간표에서 운항 여부를 확인해 주세요."
					)}
				</p>
				{official.unavailable && <div role="status"><p>운항 정보를 새로 불러오지 못했어요. {grouped.length ? "보이는 시간표는 마지막 확인본이에요." : "저장된 시간표가 없어요."}</p><button className="button secondary" type="button" onClick={official.retry}>다시 불러오기</button> <a href="/trips">저장한 여행 열기</a></div>}
				{grouped.length > 0 && <p>아래 시간은 {official.live ? "공식 정보 확인본" : "저장된 공식 시간표"} · 확인 {official.checkedAt.slice(0,10)} · 예약 전 항공사에서 다시 확인해 주세요.</p>}
				{grouped.length > 0 && (
					<details className="route-evidence">
						<summary>공식 출처·확인 시점과 운항 주의사항</summary>
						<SourceStamp
							source={
								official.live
									? "공식 운항 정보 자동 확인본"
									: origin === "ICN"
										? "인천국제공항 D+6 출발편"
										: "한국공항공사 계절 스케줄"
							}
							checkedAt={official.checkedAt}
						/>
						<p>
							편명·요일·시간이 확인된 일본 공항을 보여드려요. 운항편마다
							적용기간이 다를 수 있으니 카드와 상세의 편별 기간을 확인하고, 예약
							전에 항공사에서 다시 확인해 주세요.
						</p>
						<p>{routeSnapshotMeta.caveat}</p>
						<a href="/about/sources">수집 범위와 출처 보기 →</a>
					</details>
				)}
			</section>
			<section className="section route-filter">
				<div className="filter-chips" aria-label="출발 요일">
					<button
						type="button"
						aria-pressed={!dayFilter}
						onClick={() => setFilter("", timeFilter)}
					>
						전체 요일
					</button>
					{["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d, i) => (
						<button
							type="button"
							key={d}
							aria-pressed={dayFilter === d}
							onClick={() => setFilter(d, timeFilter)}
						>
							{weekdayOrder[i]}
						</button>
					))}
				</div>
				<div className="filter-chips" aria-label="출발 시간대">
					{[
						["", "모든 시간"],
						["am", "오전"],
						["pm", "오후"],
						["evening", "저녁"],
					].map(([v, label]) => (
						<button
							type="button"
							key={v}
							aria-pressed={timeFilter === v}
							onClick={() => setFilter(dayFilter, v!)}
						>
							{label}
						</button>
					))}
				</div>
				{!services.length && dayFilter && (
					<p>
						이 요일엔 확인된 직항이 없어요. 다른 요일:{" "}
						{["mon", "tue", "wed", "thu", "fri", "sat", "sun"]
							.map(
								(d, i) =>
									`${weekdayOrder[i]} ${official.items.filter((s) => Boolean(s.weekdayMask & (1 << i))).length}편`,
							)
							.join(" · ")}
					</p>
				)}
			</section>
			<section
				className={`section route-results ${grouped.length ? "" : "route-results--unavailable"}`}
			>
				{grouped.length === 0 ? (
					<div className="empty-state" role="status">
						<h2>
							{official.loading
								? "운항편을 불러오는 중이에요"
								: "현재 운항편 확인하기"}
						</h2>
						<p>
							정보가 보이지 않아도 운휴를 뜻하지는 않아요. 아래{" "}
							{coverageDestinations.length}곳은 {coverageCity} 도시권의 직항
							정보예요. 실제 출발공항과 운항 여부는 공식 시간표에서 확인해
							주세요.
						</p>
						<ExternalLink
							className="button primary"
							href={
								routeSources[origin === "ICN" ? 1 : 0]?.url ??
								"https://www.airport.co.kr/"
							}
						>
							공식 운항 정보 열기
						</ExternalLink>
						<a className="button secondary" href="/">
							다른 공항 고르기
						</a>
					</div>
				) : (
                    <div className="origin-route-list" aria-label={`${originInfo?.city ?? origin}에서 가는 일본 도시`}>
                    <p className="origin-route-list__start">{originInfo?.city ?? origin} <strong>{origin}</strong></p>
                    {grouped.map(({ destination, items, mask }) => {
                        const edition = editions[destination];
                        const times = items.map(item => item.departureLocal).sort();
                        const airlines = new Set(items.map(item => item.airline)).size;
                        return <article className="origin-route-row" key={destination}>
                            <a href={`/from/${origin}/to/${destination}`}><span><strong>{edition.city} <small>{destination}</small></strong><span>항공사 {airlines}곳 · {weekdaysFromMask(mask).join("·")} · {times[0]}–{times.at(-1)} 출발</span></span><span aria-hidden="true">›</span></a>
                            <small>편별 적용기간은 상세에서 확인</small>
                        </article>;
                    })}</div>
				)}
			</section>
			{coverageOnly.length > 0 && (
				<section className="section coverage-only">
					<div className="section-heading">
						<div>
							<span className="eyebrow">
								{customerSectionLabels.route.coverage}
							</span>
							<h2>
								직항은 확인됐지만,
								<br />
								공항별 편명은 다시 확인할 곳
							</h2>
						</div>
						<p>
							일본 국토교통성은 한국 출발지를 도시 단위로 공개합니다. 특히
							서울은 인천·김포를 합치므로 여기서는 취항을 단정하지 않고 공항판과
							공식 원문만 연결해요.
						</p>
					</div>
					<div className="coverage-grid">
						{coverageOnly.map((code) => (
							<article key={code}>
								<span className="iata">{code}</span>
								<h3>{editions[code].city}</h3>
								<p>{editions[code].fit}</p>
								<span className="status-badge pending">
									공항별 요일·시간 재확인
								</span>
								<a
									className="button secondary"
									href={`/airports/${code.toLowerCase()}`}
								>
									교통·숙소·일정 보기
								</a>
							</article>
						))}
					</div>
					<div className="coverage-source">
						<div>
							<strong>{gatewayCoverageSource.title}</strong>
							<p>
								유효기간 {gatewayCoverageSource.effectiveFrom}–
								{gatewayCoverageSource.effectiveTo}
							</p>
						</div>
						<ExternalLink
							className="button secondary"
							href={gatewayCoverageSource.url}
						>
							직항 데이터 원문
						</ExternalLink>
					</div>
				</section>
			)}
			<section className="section caveat">
				<h2>‘매일’도 기간 안에서만 유효해요</h2>
				<p>{routeSnapshotMeta.caveat}</p>
				<a href="/about/sources">수집 범위와 출처 보기 →</a>
			</section>
		</Layout>
	);
}

function FareTruth() {
	return (
		<section className="fare-truth" aria-labelledby="fare-title">
			<details>
				<summary>항공권 가격은 판매처에서 확인해요</summary>
				<span className="status-badge pending">가격 비교 준비 중</span>
				<h2 id="fare-title">아직 믿을 만한 가격 표본이 없어요.</h2>
				<p>
					숫자 대신 운항편과 첫 길부터 보여드릴게요. 가격 정보를 받을 준비가
					끝난 뒤에도 30개 관측값과 서로 다른 출발일 12일을 채워야
					월·요일·시간대 비교를 공개해요.
				</p>
				<dl>
					<div>
						<dt>관측 표본</dt>
						<dd>0 / 30</dd>
					</div>
					<div>
						<dt>서로 다른 출발일</dt>
						<dd>0 / 12</dd>
					</div>
					<div>
						<dt>현재 판단</dt>
						<dd>숫자 비노출</dd>
					</div>
				</dl>
			</details>
		</section>
	);
}

function RouteDetailPage({
	origin,
	destination,
}: {
	origin: OriginCode;
	destination: AirportCode;
}) {
	const edition = editions[destination];
	const originCity =
		origins.find((item) => item.iata === origin)?.city ?? origin;
	const official = useOfficialRoutes(origin, destination);
	const services = official.items;
	usePageMeta(
		`${originCity}→${edition.city} 운항과 첫 길 — 일본골목`,
		`${originCity}에서 ${edition.city}까지 운항 요일과 ${edition.airportName} 첫 이동을 확인해요.`,
	);
	return (
		<Layout>
			<section className="route-head compact paper-pattern">
				<a className="back-link" href={`/from/${origin}`}>
					← {originCity} 출발 노선
				</a>
				<span className="eyebrow">{customerSectionLabels.route.detail}</span>
				<h1>
					{originCity}에서 {edition.city},<br />
					운항편부터 첫 길까지
				</h1>
                <p>{edition.mood}</p>
                {services.length > 0 && <div className="route-brief"><p>출발 시각 · {official.live ? "공식 정보 확인본" : "저장된 공식 시간표"} · 확인 {official.checkedAt.slice(0,10)}</p><p>{[...new Set(services.flatMap(service => weekdaysFromMask(service.weekdayMask)))].join(" · ")} 운항</p><ul className="route-times">{[...new Set(services.map(service => service.departureLocal))].sort().map(time => <li key={time}>{time}</li>)}</ul><p>시각별 운항일과 적용기간은 아래 편별 시간표를 확인해 주세요.</p></div>}
                <section className="route-first-ways"><h2>도착 뒤 첫 길</h2>{edition.branches.slice(0,3).map(branch => <p key={branch.id}><strong>{branch.mode}</strong> · {branch.destination} · {branch.minutes}</p>)}<a href={`/airports/${destination.toLowerCase()}?from=${origin}`}>첫 길 자세히 보기 →</a></section>
                <div className="button-row">
					<a
						className="button primary"
						href={`/trips/new?origin=${origin}&destination=${destination}`}
						onClick={() =>
							track("trip_create_start", {
								originAirport: origin,
								destinationAirport: destination,
								surface: "route_detail",
							})
						}
					>
						이 노선으로 여행 만들기
					</a>
					<a
						className="button secondary"
						href={`/airports/${destination.toLowerCase()}?from=${origin}`}
					>
						공항판 먼저 보기
					</a>
				</div>
				{official.unavailable && <div role="status"><p>운항 정보를 새로 불러오지 못했어요. {services.length ? "표시된 시간은 마지막 확인본이에요." : "저장된 시간표가 없어요."}</p><button type="button" className="button secondary" onClick={official.retry}>다시 불러오기</button> <a href="/trips">저장한 여행 열기</a></div>}
				{services.length > 0 && (
					<SourceStamp
						source={
							official.live
								? "공식 운항 정보 자동 확인본"
								: origin === "ICN"
									? "인천국제공항 D+6 출발편"
									: "한국공항공사 계절 스케줄"
						}
						checkedAt={official.checkedAt}
					/>
				)}
			</section>
			<EditionPreview edition={edition} origin={origin} />
			<section className="section split-layout">
				<div>
					<div className="section-heading">
						<div>
							<span className="eyebrow">
								{customerSectionLabels.route.schedule}
							</span>
							<h2>출발 시간표</h2>
						</div>
						<p>항공사 예약 전에 다시 확인해 주세요.</p>
					</div>
					<div className="schedule-list">
						{services.length ? (
							services.map((service) => (
								<article key={service.id}>
									<time>{service.departureLocal}</time>
									<div>
										<strong>
											{service.airline} {service.flightNumber}
										</strong>
										<span>
											{weekdaysFromMask(service.weekdayMask).join("·")} ·{" "}
											{timeBucket(service.departureLocal)}
										</span>
										<span>
											적용 {service.effectiveFrom}–{service.effectiveTo}
										</span>
									</div>
									<span className="status-badge current">
										{official.live ? "자동 확인" : "확인본"}
									</span>
								</article>
							))
						) : (
							<div className="empty-state" role="status">
								<h3>
									{official.loading
										? "현재 운항편을 불러오는 중이에요"
										: "현재 운항편을 확인하지 못했어요"}
								</h3>
								<p>
									운휴를 뜻하지 않아요. 아래 공식 시간표에서 예약 전 운항 여부를
									확인해 주세요.
								</p>
								<ExternalLink
									className="button secondary"
									href={
										routeSources[origin === "ICN" ? 1 : 0]?.url ??
										"https://www.airport.co.kr/"
									}
								>
									공식 운항 정보 열기
								</ExternalLink>
							</div>
						)}
					</div>
				</div>
				<aside>
					<FareTruth />
				</aside>
			</section>
		</Layout>
	);
}

function EditionPreview({
	edition,
	origin,
}: {
	edition: Edition;
	origin?: OriginCode;
}) {
	const first = edition.branches[0];
	if (!first) return null;
	return (
		<section className="section arrival-preview">
			<div>
				<span className="eyebrow">{customerSectionLabels.route.arrival}</span>
				<h2>
					{edition.city}에 내리면,
					<br />
					{first.destination}까지 어떻게 갈까요?
				</h2>
				<p>
					{first.mode} · {first.minutes}
				</p>
			</div>
			<div className="route-slip">
				<span>추천 첫 길</span>
				<strong>{first.destination}</strong>
				<p>{first.boarding}</p>
				<p className="fallback">놓쳤다면: {first.fallback}</p>
				<a
					className="button primary"
					href={`/airports/${edition.iata.toLowerCase()}${origin ? `?from=${origin}` : ""}`}
				>
					첫 길 3가지 비교
				</a>
			</div>
		</section>
	);
}

function SaveCardButton({
	edition,
	branch,
	sourceUrl = edition.accessSource,
	checkedAt = routeSnapshotMeta.checkedAt,
}: {
	edition: Edition;
	branch: Edition["branches"][number];
	sourceUrl?: string;
	checkedAt?: string;
}) {
	const [state, setState] = useState<"idle" | "saving" | "saved" | "error">(
		"idle",
	);
	const onSave = async () => {
		setState("saving");
		try {
			await saveCard({
				schemaVersion: 1,
				id: `${edition.iata}-${branch.id}`,
				airport: edition.iata,
				city: edition.city,
				branch,
				sourceUrl,
				checkedAt,
				savedAt: new Date().toISOString(),
			});
			track("arrival_card_save", { destinationAirport: edition.iata });
			setState("saved");
		} catch {
			setState("error");
		}
	};
	return (
		<>
			<button
				className="button primary"
				type="button"
				disabled={state === "saving" || state === "saved"}
				onClick={onSave}
			>
				{state === "saving"
					? "저장하는 중…"
					: state === "saved"
						? "도착카드를 저장했어요"
						: "이 첫 길 저장하기"}
			</button>
			{state === "error" && (
				<p role="alert">
					이 기기의 저장소에 담지 못했어요. 화면은 그대로 남아 있어요.
				</p>
			)}
		</>
	);
}

function EditionPage({ code }: { code: AirportCode }) {
	const edition = editions[code];
	const photos = editionPhotos(code);
	const cityGuide = cityGuides[code];
	const from = new URLSearchParams(window.location.search).get("from");
	const originQuery =
		from && origins.some((origin) => origin.iata === from)
			? `&origin=${from}`
			: "";
	usePageMeta(
		`${code} ${edition.city} 공항판 — 일본골목`,
		`${edition.airportName}에서 시내로 가는 첫 길과 먹거리, 물가, 장소를 확인해요.`,
	);
	useEffect(
		() => track("airport_edition_return", { destinationAirport: code }),
		[code],
	);
	return (
		<Layout>
			<section className="edition-hero">
				<div className="edition-hero-copy">
					<a className="back-link" href="/">
						← 모든 공항판
					</a>
					<span className="iata giant" aria-hidden="true">
						{code}
					</span>
					<span className="eyebrow">{edition.airportName}</span>
					<h1>
						{edition.city}에 내리면,
						<br />첫 동네는
						<span className="mobile-title-break">
							<br />
						</span>{" "}
						이렇게 열려요.
					</h1>
					<p>{edition.mood}</p>
					<div className="fit">이런 여행에 맞아요 · {edition.fit}</div>
					<div className="button-row">
						<a
							className="button primary"
							href={`/trips/new?destination=${code}${originQuery}`}
						>
							이 도시로 여행 만들기
						</a>
					</div>
					<SourceStamp
						source="공항 운영사 접근 안내"
						checkedAt={cityGuide.tourismSource.checkedAt}
					/>
				</div>
				{photos[0] ? (
					<figure>
						<img
							src={photos[0].publicPath}
							srcSet={responsivePhoto(photos[0].publicPath)}
							sizes="(max-width: 980px) 100vw, 50vw"
							alt={`${photos[0].subject}의 실제 풍경`}
						/>
						<figcaption>
							{photos[0].subject} · {photos[0].attributionText}
						</figcaption>
					</figure>
				) : (
					<div
						className="photo-placeholder"
						role="img"
						aria-label={`${edition.city} 사진 권리를 다시 확인하는 중`}
					>
						<BrandMark size={80} />
						<strong>사진 권리를 다시 확인하고 있어요.</strong>
						<span>공항판 정보와 공식 링크는 그대로 볼 수 있어요.</span>
					</div>
				)}
			</section>
			<nav className="airport-section-nav" aria-label="공항 가이드 목차">
				<a href="#transport">첫 이동</a>
				<a href="#stay">숙소 동네</a>
				<a href="#duration">일정</a>
				<a href="#local-picks">먹고 걷기</a>
				<a href={`/walks?airport=${code}`}>반나절 코스</a>
			</nav>
			<CityGuideSections
				guide={cityGuide}
				city={edition.city}
				code={code}
				originQuery={originQuery}
				saveAction={
					(choice) => <SaveCardButton edition={edition} branch={transportChoiceToBranch(choice)} sourceUrl={choice.operatorUrl} checkedAt={`${choice.checkedAt}T00:00:00+09:00`} />
				}
			/>
			<section className="section guide-grid">
				<article className="guide-block food">
					<span className="eyebrow">{customerSectionLabels.edition.eat}</span>
					<h2>이 공항판에서 먹어야 할 것</h2>
					<ul>
						{edition.foods.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				</article>
				<article className="guide-block prices">
					<span className="eyebrow">
						{customerSectionLabels.edition.budget}
					</span>
					<h2>하루 예산을 잡는 물가 기준</h2>
					<ul>
						{Object.values(edition.prices).map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
					<p>월 1회 실제 메뉴와 공식 요금을 다시 보는 편집 범위예요.</p>
				</article>
				<article className="guide-block">
					<span className="eyebrow">
						{customerSectionLabels.edition.bringHome}
					</span>
					<h2>지역을 기억하는 특산물</h2>
					<ul>
						{edition.specialties.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				</article>
				<article className="guide-block">
					<span className="eyebrow">
						{customerSectionLabels.edition.walkFurther}
					</span>
					<h2>첫 동네에서 더 가볼 곳</h2>
					<ul>
						{edition.spots.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				</article>
			</section>
			{photos[1] && (
				<section className="wide-photo">
					<figure>
						<img
							src={photos[1].publicPath}
							srcSet={responsivePhoto(photos[1].publicPath)}
							sizes="100vw"
							alt={`${photos[1].subject}의 실제 풍경`}
							loading="lazy"
						/>
						<figcaption>
							<strong>{photos[1].subject}</strong>
							<span>{photos[1].attributionText}</span>
							<ExternalLink href={photos[1].sourceUrl}>
								사진 원문·라이선스
							</ExternalLink>
						</figcaption>
					</figure>
				</section>
			)}
			<section className="section correction-cta">
				<div>
					<span className="eyebrow">
						{customerSectionLabels.edition.correction}
					</span>
					<h2>시간표가 달라졌나요?</h2>
					<p>공식 링크와 함께 알려주면 자동으로 반영하지 않고 먼저 확인해요.</p>
				</div>
				<a className="button secondary" href={`/correct?airport=${code}`}>
					근거와 함께 알려주기
				</a>
			</section>
			<PublicGuideAd surface="airport_end" />
		</Layout>
	);
}

function CardsPage() {
	const [cards, setCards] = useState<ArrivalCard[] | null>(null);
	const [error, setError] = useState(false);
	usePageMeta(
		"저장한 도착카드 — 일본골목",
		"인터넷이 없어도 다시 보는 공항 첫 이동 카드",
	);
	useEffect(() => {
		listCards()
			.then(setCards)
			.catch(() => setError(true));
	}, []);
	const remove = async (id: string) => {
		if (!window.confirm("이 도착카드를 이 기기에서 지울까요?")) return;
		await removeCard(id);
		setCards((current) => current?.filter((card) => card.id !== id) ?? []);
	};
	return (
		<Layout>
			<section className="route-head compact">
				<span className="eyebrow">
					{customerSectionLabels.edition.savedCards}
				</span>
				<h1>저장한 첫 길이에요.</h1>
				<p>
					계정 없이 이 기기에만 보관해요. 새 확인본과 달라지면 출처를 다시
					열어주세요.
				</p>
			</section>
			<section className="section">
				{cards === null && !error && (
					<div className="skeleton-list" aria-label="도착카드를 불러오는 중" />
				)}
				{error && (
					<div className="empty-state">
						<h2>이 기기의 저장소를 읽지 못했어요.</h2>
						<p>브라우저의 사이트 데이터 허용 여부를 확인해 주세요.</p>
					</div>
				)}
				{cards?.length === 0 && (
					<div className="empty-state">
						<BrandMark size={64} />
						<h2>저장한 도착카드가 없어요.</h2>
						<p>
							공항판에서 첫 길을 저장하면 인터넷이 없어도 다시 볼 수 있어요.
						</p>
						<a className="button primary" href="/">
							공항판 고르기
						</a>
					</div>
				)}
				<div className="saved-grid">
					{cards?.map((card) => (
						<article className="saved-card" key={card.id}>
							<span className="iata">{card.airport}</span>
							<h2>
								{card.city} · {card.branch.destination}
							</h2>
							<p>
								{card.branch.mode} · {card.branch.minutes}
							</p>
							<p>{card.branch.boarding}</p>
							<p className="fallback">놓쳤다면: {card.branch.fallback}</p>
							<SourceStamp
								source="저장 당시 공식 접근 안내"
								checkedAt={card.checkedAt}
							/>
							<div>
								<a
									className="button text"
									href={`/cards/${encodeURIComponent(card.id)}`}
								>
									카드 자세히 보기
								</a>
								<button
									className="text-button danger"
									type="button"
									onClick={() => remove(card.id)}
								>
									삭제
								</button>
							</div>
						</article>
					))}
				</div>
			</section>
		</Layout>
	);
}

function CardDetailPage({ id }: { id: string }) {
	const [card, setCard] = useState<ArrivalCard | null | undefined>(undefined);
	const [action, setAction] = useState<"idle" | "shared" | "copied" | "error">(
		"idle",
	);
	usePageMeta(
		"도착카드 자세히 — 일본골목",
		"저장한 공항 첫 이동을 오프라인에서 다시 보고 공유해요.",
	);
	useEffect(() => {
		getCard(id)
			.then((value) => {
				setCard(value ?? null);
				if (value)
					track("offline_card_open", { destinationAirport: value.airport });
			})
			.catch(() => setCard(null));
	}, [id]);
	if (card === undefined)
		return (
			<Layout>
				<section className="section">
					<div className="skeleton-list" aria-label="도착카드를 불러오는 중" />
				</section>
			</Layout>
		);
	if (card === null)
		return (
			<Layout>
				<section className="empty-state page-not-found">
					<BrandMark size={64} />
					<h1>이 기기에 카드가 없어요.</h1>
					<p>도착카드는 계정 없이 이 기기에만 저장돼요.</p>
					<a className="button primary" href="/cards">
						저장한 카드 보기
					</a>
				</section>
			</Layout>
		);
	const hasNewerCheck =
		new Date(routeSnapshotMeta.checkedAt).getTime() >
		new Date(card.checkedAt).getTime();
	const requiresRefresh = hasNewerCheck || needsCardRefresh(card.checkedAt);
	const share = async () => {
		const text = `${card.airport} ${card.city} → ${card.branch.destination}\n${card.branch.mode} · ${card.branch.minutes}\n${card.branch.boarding}\n놓쳤다면: ${card.branch.fallback}`;
		try {
			if (navigator.share) {
				await navigator.share({
					title: `일본골목 ${card.airport} 도착카드`,
					text,
				});
				setAction("shared");
			} else if (navigator.clipboard) {
				await navigator.clipboard.writeText(text);
				setAction("copied");
			} else throw new Error("share unavailable");
			track("arrival_card_share", { destinationAirport: card.airport });
		} catch {
			setAction("error");
		}
	};
	const download = () => {
		const url = URL.createObjectURL(
			new Blob([serializeCard(card)], { type: "application/json" }),
		);
		const link = document.createElement("a");
		link.href = url;
		link.download = `japangolmok-${card.id}.json`;
		link.click();
		URL.revokeObjectURL(url);
		track("arrival_card_export", { destinationAirport: card.airport });
	};
	return (
		<Layout>
			<section className="route-head compact">
				<a className="back-link" href="/cards">
					← 저장한 도착카드
				</a>
				<span className="eyebrow">
					{customerSectionLabels.edition.savedCard}
				</span>
				<h1>
					{card.city}에 내리면,
					<br />
					{card.branch.destination}으로.
				</h1>
				<p>
					{card.branch.mode} · {card.branch.minutes}
				</p>
			</section>
			<section className="section card-detail">
				<article className="route-slip">
					<span className="iata">{card.airport}</span>
					<h2>{card.branch.destination}</h2>
					<p>{card.branch.boarding}</p>
					<p className="fallback">
						<strong>놓쳤다면</strong> {card.branch.fallback}
					</p>
					<dl>
						<div>
							<dt>요금</dt>
							<dd>{card.branch.fare}</dd>
						</div>
						<div>
							<dt>저장 시각</dt>
							<dd>
								{new Intl.DateTimeFormat("ko-KR", {
									dateStyle: "medium",
									timeStyle: "short",
								}).format(new Date(card.savedAt))}
							</dd>
						</div>
					</dl>
				</article>
				<aside className="card-freshness">
					<span
						className={`status-badge ${requiresRefresh ? "pending" : "current"}`}
					>
						{hasNewerCheck
							? "새 확인본 있음"
							: requiresRefresh
								? "72시간 재확인 필요"
								: "저장본과 같은 확인 시점"}
					</span>
					<h2>
						{requiresRefresh
							? "출발 전에 현재 원문을 다시 열어주세요."
							: "그래도 이동 직전 공식 시간을 확인해 주세요."}
					</h2>
					<SourceStamp
						source="저장 당시 공식 접근 안내"
						checkedAt={card.checkedAt}
					/>
					<ExternalLink className="button secondary" href={card.sourceUrl}>
						현재 공식 시간표 열기
					</ExternalLink>
				</aside>
				<div className="card-actions">
					<button className="button primary" type="button" onClick={share}>
						이동 요약 공유하기
					</button>
					<button className="button secondary" type="button" onClick={download}>
						도착카드 파일 받기
					</button>
					{action !== "idle" && (
						<p role="status">
							{action === "shared"
								? "공유 창을 열었어요."
								: action === "copied"
									? "이동 요약을 복사했어요."
									: "공유 창을 열지 못했어요. 도착카드 파일로 보관할 수 있어요."}
						</p>
					)}
				</div>
			</section>
		</Layout>
	);
}

function ChangesPage() {
	usePageMeta(
		"변경 기록 — 일본골목",
		"공식 운항 확인본과 공항판의 변경 상태를 확인해요.",
	);
	useEffect(() => track("route_change_seen"), []);
	return (
		<Layout>
			<section className="route-head compact">
				<span className="eyebrow">달라진 정보</span>
				<h1>
					달라진 사실만
					<br />
					다시 볼 수 있게.
				</h1>
				<p>
					공식 운항 정보를 자동으로 확인할 준비가 끝나지 않아, 지금은 공식
					웹에서 직접 확인한 내용이 첫 기록이에요. 이전 내용과 비교할 변화는
					아직 없어요.
				</p>
				<SourceStamp source="운항 확인본" />
			</section>
			<section className="section changes-grid">
				<article>
					<span className="status-badge current">첫 기록</span>
					<h2>한국 출발 운항 {routes.length}편</h2>
					<p>
						{routeSources.map((source) => source.publisher).join(" · ")}에서
						직접 확인한 내용이에요.
					</p>
					<a href="/about/sources">출처와 범위 보기 →</a>
				</article>
				<article>
					<span className="status-badge current">매주 확인</span>
					<h2>공항 이동 공식 안내</h2>
					<p>
						30개 공항 운영사·공식 관광 페이지가 바뀌면 자동으로 반영하지 않고
						먼저 확인해요. 먹거리·물가는 매달 근거를 다시 살펴요.
					</p>
					<a href="/status">갱신 상태 보기 →</a>
				</article>
				<article>
					<span className="status-badge pending">가격 정보 준비 중</span>
					<h2>항공권 가격</h2>
					<p>
						가격 정보를 받을 준비와 표본 기준을 채우기 전에는 변경률이나 싸다는
						판단도 공개하지 않아요.
					</p>
					<a href="/from/PUS/to/FUK">가격 표본 기준 보기 →</a>
				</article>
			</section>
		</Layout>
	);
}

function SourcesPage() {
	usePageMeta(
		"출처와 확인일 — 일본골목",
		"운항과 공항판 출처, 확인 범위와 최신 상태를 공개해요.",
	);
	return (
		<Layout>
			<section className="route-head compact">
				<span className="eyebrow">확인한 출처</span>
				<h1>어떤 출처를 언제 확인했는지 공개해요.</h1>
				<p>‘최신’이라는 말보다 확인일과 범위를 먼저 보여드려요.</p>
			</section>
			<section className="section source-list">
				<h2>운항 스케줄</h2>
				{routeSources.map((source) => (
					<article key={source.id}>
						<span className="status-badge current">공식</span>
						<div>
							<h3>{source.publisher}</h3>
							<p>{source.scope}</p>
							<SourceStamp source={`${source.publisher} 운항 자료`} />
						</div>
						<ExternalLink href={source.url}>원문</ExternalLink>
					</article>
				))}
				<h2>공항에서 첫 동네까지</h2>
				{destinationCodes.map((code) => (
					<article key={code}>
						<span className="iata">{code}</span>
						<div>
							<h3>{editions[code].airportName}</h3>
							<p>
								{editions[code].branches.length}가지 이동 방법 · 확인{" "}
								{cityGuides[code].tourismSource.checkedAt}
							</p>
						</div>
						<ExternalLink href={editions[code].accessSource}>
							운영사 원문
						</ExternalLink>
					</article>
				))}
				<h2>입국·안전·현지 이동</h2>
				{officialTravelTools.map((tool) => (
					<article key={tool.id}>
						<span className="status-badge current">공식 연결</span>
						<div>
							<h3>{tool.title}</h3>
							<p>{tool.description}</p>
							<SourceStamp
								source={
									tool.kind === "entry"
										? "일본 디지털청"
										: tool.kind === "safety"
											? "일본 정부·JNTO"
											: "공식·전문 이동 서비스"
								}
								checkedAt="2026-08-10T00:00:00+09:00"
							/>
						</div>
						<ExternalLink href={tool.url}>현재 페이지</ExternalLink>
					</article>
				))}
			</section>
		</Layout>
	);
}

function CorrectionPage() {
	const params = new URLSearchParams(window.location.search);
	const [state, setState] = useState<"idle" | "sending" | "received" | "error">(
		"idle",
	);
	const [validation, setValidation] = useState<{
		field: "sourceUrl" | "message";
		message: string;
	} | null>(null);
	usePageMeta(
		"정정 제보 — 일본골목",
		"공식 링크와 달라진 내용을 일본골목에 알려주세요.",
	);
	const showValidation = (field: "sourceUrl" | "message", message: string) => {
		setValidation({ field, message });
		requestAnimationFrame(() =>
			document.querySelector<HTMLElement>(`[name="${field}"]`)?.focus(),
		);
	};
	const submit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const sourceUrl = event.currentTarget.elements.namedItem("sourceUrl");
		const message = event.currentTarget.elements.namedItem("message");
		if (
			!(sourceUrl instanceof HTMLInputElement) ||
			!sourceUrl.value.trim() ||
			!sourceUrl.validity.valid
		) {
			showValidation(
				"sourceUrl",
				"확인할 수 있는 공식 근거 URL을 입력해 주세요.",
			);
			return;
		}
		if (
			!(message instanceof HTMLTextAreaElement) ||
			message.value.trim().length < 10
		) {
			showValidation("message", "달라진 내용을 10자 이상 입력해 주세요.");
			return;
		}
		setValidation(null);
		setState("sending");
		const data = Object.fromEntries(new FormData(event.currentTarget));
		localStorage.setItem("jgm-correction-draft", JSON.stringify(data));
		try {
			const response = await fetch(apiUrl("/api/v1/corrections"), {
				method: "POST",
				headers: {
					"content-type": "application/json",
					"Idempotency-Key": crypto.randomUUID(),
				},
				body: JSON.stringify({
					entityType: "content",
					entityId: String(data.airport || "general"),
					sourceUrl: data.sourceUrl,
					message: data.message,
					website: data.website,
				}),
			});
			if (!response.ok) throw new Error();
			localStorage.removeItem("jgm-correction-draft");
			track("correction_submit", {
				destinationAirport: String(data.airport || ""),
			});
			setState("received");
		} catch {
			setState("error");
			requestAnimationFrame(() =>
				document.getElementById("correction-submit-error")?.focus(),
			);
		}
	};
	const errorId = validation ? "correction-form-error" : undefined;
	return (
		<Layout>
			<section className="route-head compact">
				<span className="eyebrow">{customerSectionLabels.correction}</span>
				<h1>시간표가 달라졌나요?</h1>
				<p>
					근거 링크와 달라진 내용을 알려주세요. 자동 반영하지 않고 검토해요.
				</p>
			</section>
			<section className="section form-section">
				{state === "received" ? (
					<div className="empty-state">
						<h2>근거를 받았어요.</h2>
						<p>공식 출처와 대조한 뒤 변경 이력에 남길게요.</p>
						<a className="button primary" href="/">
							홈으로
						</a>
					</div>
				) : (
					<form noValidate onSubmit={submit}>
						<label>
							공항
							<select
								name="airport"
								defaultValue={params.get("airport") ?? "general"}
							>
								<option value="general">전체·기타</option>
								{destinationCodes.map((code) => (
									<option key={code}>{code}</option>
								))}
							</select>
						</label>
						<label>
							<span>
								공식 근거 URL <strong className="required-text">필수</strong>
							</span>
							<input
								name="sourceUrl"
								type="url"
								required
								aria-required="true"
								aria-invalid={
									validation?.field === "sourceUrl" ? "true" : undefined
								}
								aria-describedby={
									validation?.field === "sourceUrl" ? errorId : undefined
								}
								aria-errormessage={
									validation?.field === "sourceUrl" ? errorId : undefined
								}
								onChange={() =>
									validation?.field === "sourceUrl" && setValidation(null)
								}
								placeholder="https://…"
							/>
						</label>
						<label>
							<span>
								달라진 내용 <strong className="required-text">필수</strong>
							</span>
							<textarea
								name="message"
								minLength={10}
								maxLength={1000}
								required
								aria-required="true"
								aria-invalid={
									validation?.field === "message" ? "true" : undefined
								}
								aria-describedby={
									validation?.field === "message" ? errorId : undefined
								}
								aria-errormessage={
									validation?.field === "message" ? errorId : undefined
								}
								onChange={() =>
									validation?.field === "message" && setValidation(null)
								}
								rows={7}
							/>
						</label>
						{validation && (
							<p className="inline-error" id={errorId} role="alert">
								{validation.message}
							</p>
						)}
						<label className="honeypot" aria-hidden="true">
							웹사이트
							<input name="website" tabIndex={-1} autoComplete="off" />
						</label>
						<button
							type="submit"
							className="button primary"
							disabled={state === "sending"}
						>
							{state === "sending" ? "보내는 중…" : "검토 요청 보내기"}
						</button>
						{state === "error" && (
							<p
								className="inline-error"
								id="correction-submit-error"
								role="alert"
								tabIndex={-1}
							>
								지금은 제보를 받을 수 없어요. 작성한 내용은 이 기기에
								남겨뒀어요.
							</p>
						)}
					</form>
				)}
			</section>
		</Layout>
	);
}

export const legalContent = {
	privacy: {
		title: "필요한 정보만 다뤄요.",
		body: [
			"보내기를 누른 내용만 공유 링크에 담깁니다. 링크 fragment의 일정은 일본골목 서버에 저장되지 않지만 링크를 가진 사람과 메신저는 볼 수 있습니다. 금액·자유메모·사진·숙소 주소는 기본 제외합니다. 식사 카드는 기기에만 저장하며 공유 링크·요약 카드에는 포함하지 않습니다. 전체 백업에는 식사 카드도 포함되므로 안전하게 보관해 주세요.",
			"이 기기 기록은 내 여행의 ‘기기 바꿀 때’ 또는 이 페이지에서 전체 백업 후 지울 수 있습니다. 최근 지운 여행은 7일 동안 복구할 수 있지만 전부 지우기는 최근 삭제 기록도 함께 지웁니다.",
			"앱 오류 신고는 사용자가 쓴 글과 화면 종류·브라우저 종류를 Cloudflare 서버로 전송하며 90일 안에 삭제합니다. 서버 처리 수탁자는 Cloudflare, Inc.(미국 소재)입니다. 해외 인프라를 통한 처리를 원하지 않으면 제보·신고를 보내지 않고 기기 내 여행 기능을 이용할 수 있습니다. D1의 실제 저장 지역은 공급자가 관리하며 현재 설정만으로 특정 국가에 저장됨을 보장하지 않습니다. 이용 이벤트에도 개인정보·여행 원문을 담지 않습니다.",
			"일본골목은 회원가입이나 정확한 위치를 요구하지 않습니다. 여행 날짜·일정·준비·예산·가격 메모·도착카드와 골목 필름 사진은 이 브라우저의 저장공간에만 보관되고 서버로 전송되지 않습니다. 사진은 사용자가 골목 필름 파일을 직접 받을 때만 그 파일에 포함되며, 여행 사본은 선택한 항목만 포함하며, 전체 기기 백업은 사진 포함 여부를 직접 고를 수 있습니다.",
			"골목 필름은 사용자가 선택한 일정·동네·가게와 사진을 날짜순으로 보여 줄 뿐, 얼굴 인식·동행 또는 아동 추론·신체나 실력 평가·가게 자동 평가를 하지 않습니다. 정확한 위치나 사진 원본을 동의 없이 공개하거나 외부 서비스로 전송하지 않습니다.",
			"서비스 품질 측정을 위해 허용된 이용 항목 이름, 이용 경로, 공항 코드, 여행 단계, 기능 종류, 발생 시각만 다룰 수 있습니다. 여행을 구분하는 값·제목·날짜·금액·검색어·자유 입력·정확한 위치·사진·접속 주소 원문·기기 광고 식별값은 분석 정보에 넣지 않습니다.",
			"앱인토스에서는 공개 공항·반나절 가이드 끝에서 사용자가 광고 보기를 선택한 경우 토스 광고 SDK가 동작할 수 있습니다. 토스 광고 시스템의 기기·광고 식별정보 처리에는 해당 플랫폼의 개인정보 처리 규칙이 적용됩니다. 일본골목은 광고 SDK에 여행 이름·ID·날짜·금액·메모·사진·정확한 위치를 전달하거나 기기 광고 식별값을 자체 서버에 저장하지 않습니다. 광고 노출 제한 시각은 이 기기에만 보관하며, 광고 그만 보기를 누르거나 화면을 벗어나면 배너 요청을 종료합니다. 광고를 선택하지 않아도 여행 기능은 그대로 이용할 수 있습니다.",
			"Google 지도는 사용자가 ‘지도 불러오기’를 누른 뒤에만 외부 화면으로 연결됩니다. 이때 Google에 네트워크 요청과 일반적인 접속 정보가 전달될 수 있으며 Google의 개인정보처리방침이 적용됩니다. 일본골목은 현재 위치 권한을 요청하거나 지도 이용 위치를 저장하지 않고, 지도 없이도 공식 링크와 길찾기 링크를 선택할 수 있습니다.",
			"정정 제보를 보낼 때 입력한 근거 URL과 내용은 검토 목적으로 처리합니다. 개인정보나 예약번호를 적지 마세요. 처리 목적이 끝난 제보는 90일 안에 삭제합니다.",
		],
	},
	terms: {
		title: "정보는 판단을 돕고, 예약을 대신하지 않아요.",
		body: [
			"일본골목은 여행 일정·준비·예산과 현지 사용을 돕지만 항공권·교통권을 판매하거나 발권·결제·환불을 수탁하지 않는 정보 서비스입니다.",
			"운항편, 운임, 막차, 교통 상황, 날씨와 영업 시간은 바뀔 수 있습니다. 예약이나 이동 직전 항공사와 공항·교통·시설 운영사의 공식 정보를 다시 확인해야 합니다. 일본골목의 지도 링크는 출발지부터 목적지까지의 실시간 길안내를 대신하지 않습니다.",
			"개인 가격 메모는 사용자가 본 조건을 보관하는 기능이지 시장 최저가나 추천 시점을 뜻하지 않습니다. 시장 가격 비교는 가격 정보를 받을 준비가 끝나고 충분한 표본이 모였을 때만 제공하며 최저가를 보장하지 않습니다. 사진은 각 표시된 이용 조건과 저작자 조건을 따릅니다.",
		],
	},
	support: {
		title: "일본골목에 알려주세요.",
		body: [
			"잘못된 운항·이동 정보와 일반 문의, 사진 권리 침해 신고는 현재 정정 제보 양식에서 함께 받습니다. 공식 근거 URL을 첨부하고 예약번호·전화번호 같은 개인정보는 적지 마세요.",
			"접수 내용은 자동으로 반영하지 않고 먼저 확인하며, 처리 목적이 끝나면 90일 안에 삭제합니다.",
			"긴급한 운항·공항 문제는 일본골목이 처리할 수 없으므로 항공사 또는 공항 운영사에 직접 문의해야 합니다.",
		],
	},
};

export const legalCompanionNotices = {
	privacy:
		"동행 모집문은 이 브라우저에만 저장합니다. 공유·복사를 선택하면 모집문에 적은 항목만 전달하며, 개인 여행·예산·사진·도착카드는 포함하지 않습니다. 외부 앱에 게시한 글은 이 기기에서 수정하거나 삭제해도 바뀌지 않습니다.",
	terms:
		"동행 기능은 모집문 작성과 공유를 돕습니다. 동행 성사·참여자 신원·안전을 보장하거나 결제를 중개하지 않습니다. 처음에는 공개된 장소에서 만나고 비용은 각자 결제하세요. 선입금·숙소 주소·예약번호·연락처를 모집문에 적지 마세요.",
	support:
		"오픈카톡 게시물은 해당 대화방의 신고·차단 기능을 이용하세요. 일본골목의 문의 양식은 긴급 구조나 실시간 동행 중재 창구가 아닙니다.",
};

function LegalPage({ kind }: { kind: keyof typeof legalContent }) {
	const content = legalContent[kind];
	usePageMeta(`${content.title} — 일본골목`, content.body[0] ?? content.title);
	const companionNotice = legalCompanionNotices[kind];
	return (
		<Layout>
			<article className="legal">
				<span className="eyebrow">{legalSectionLabels[kind]} · 2026-09-08</span>
				<h1>{content.title}</h1>
				{content.body.map((paragraph) => (
					<p key={paragraph}>{paragraph}</p>
				))}
				<h2>동행 모집문과 외부 공유</h2>
				<p>{companionNotice}</p>
				{kind === "privacy" && <DeviceDataPanel />}
				{kind === "support" && <AppIssueForm />}
				{kind === "support" && (
					<a className="button primary" href="/correct">
						정정 제보 열기
					</a>
				)}
			</article>
		</Layout>
	);
}

function StatusPage() {
	usePageMeta("정보 상태 — 일본골목", "정보별 출처와 최신 상태를 확인해요.");
	const [automation, setAutomation] = useState<AutomationStatus | null>(null);
	useEffect(() => {
		fetch(apiUrl("/api/v1/automation/status"), {
			headers: { accept: "application/json" },
		})
			.then((response) => (response.ok ? response.json() : Promise.reject()))
			.then((payload) => setAutomation(readAutomationStatus(payload)))
			.catch(() => setAutomation(readAutomationStatus(null)));
	}, []);
	const rows = [
		[
			"한국공항공사 운항",
			"직접 확인한 운항 정보",
			"2026-08-09",
			"자동 확인 준비가 끝나면 6시간마다 확인",
		],
		[
			"인천공항 운항",
			"7일 동안 직접 확인한 정보",
			"2026-08-09",
			"자동 확인 준비가 끝나면 6시간마다 확인",
		],
		[
			"가격 요일·월·시간대",
			"가격 정보 준비 중",
			"아직 비교 숫자 없음",
			"가격 정보 사용 준비와 충분한 표본을 갖춘 뒤 공개",
		],
		[
			"공항 첫 이동",
			"30개 공항 편집 확인본",
			gatewayCoverageSource.checkedAt,
			"매달 다시 확인 · 공식 페이지가 바뀌면 검토 후 반영",
		],
		[
			"먹거리·물가·장소",
			"30개 공항 편집 확인본",
			gatewayCoverageSource.checkedAt,
			"월 1회 근거 재검토",
		],
		["장소 사진", "권리 확인", "38장", "권리 분쟁 시 즉시 비노출"],
		[
			"공식 정보 자동 확인",
			automationHeadline(automation),
			automationSummary(automation),
			"6시간마다 다시 확인 · 실패하면 이전 확인본 유지",
		],
		[
			"내 여행·일정·예산·골목 필름",
			"이 기기에 보관",
			"계정 없이 이 브라우저에 저장",
			"사진 서버 전송 없음 · 필요할 때 파일로 보관",
		],
	];
	return (
		<Layout>
			<section className="route-head compact">
				<span className="eyebrow">정보 확인 현황</span>
				<h1>정보별 최신 상태를 확인해요.</h1>
				<p>자동 확인 준비와 실제 확인 결과를 나눠 보여드려요.</p>
			</section>
			<section
				className="section status-table"
				role="table"
				aria-label="정보 최신 상태"
			>
				<div className="status-row header" role="row">
					<span>정보</span>
					<span>상태</span>
					<span>근거</span>
					<span>다음 갱신</span>
				</div>
				{rows.map((row) => (
					<div className="status-row" role="row" key={row[0]}>
						{row.map((cell, index) => (
							<span
								role="cell"
								data-label={["정보", "상태", "근거", "다음 갱신"][index]}
								key={cell}
							>
								{cell}
							</span>
						))}
					</div>
				))}
			</section>
		</Layout>
	);
}

function NotFoundPage() {
	usePageMeta(
		"길을 찾지 못했어요 — 일본골목",
		"요청한 일본골목 페이지가 없어요.",
	);
	return (
		<Layout>
			<section className="empty-state page-not-found">
				<BrandMark size={80} />
				<h1>이 골목은 아직 열리지 않았어요.</h1>
				<p>출발공항이나 공항판에서 다시 시작해 주세요.</p>
				<a className="button primary" href="/">
					홈으로
				</a>
			</section>
		</Layout>
	);
}

export function App() {
	const path = window.location.pathname.replace(/\/$/, "") || "/";
	const companionPath = path;
	if (companionPath === "/companions") return <CompanionListPage />;
	if (companionPath === "/companions/new") return <CompanionEditorPage />;
	const companionEdit = companionPath.match(
		/^\/companions\/([a-zA-Z0-9-]+)\/edit$/,
	);
	if (companionEdit?.[1]) return <CompanionEditorPage id={companionEdit[1]} />;
	const companionDetail = companionPath.match(
		/^\/companions\/([a-zA-Z0-9-]+)$/,
	);
	if (companionDetail?.[1])
		return <CompanionDetailPage id={companionDetail[1]} />;
	if (path.startsWith("/guides/") && path.slice(8) in guideTitles)
		return <GuidePage slug={path.slice(8) as keyof typeof guideTitles} />;
	if (path === "/s" || new URLSearchParams(location.search).has("d"))
		return <SharePage />;
	if (path === "/") return <TravelHome />;
	if (path === "/flights") return <HomePage />;
	if (path === "/today") return <TodayLandingPage />;
	if (path === "/cards") return <CardsPage />;
	if (path === "/walks") return <WalkListPage />;
	if (path.startsWith("/walks/"))
		return <WalkDetailPage slug={decodeURIComponent(path.slice(7))} />;
	if (path === "/trips") return <TripListPage />;
	if (path === "/trips/new") return <TripCreatePage />;
	if (path === "/changes") return <ChangesPage />;
	if (path === "/about/sources") return <SourcesPage />;
	if (path === "/correct") return <CorrectionPage />;
	if (path === "/privacy" || path === "/terms" || path === "/support")
		return <LegalPage kind={path.slice(1) as keyof typeof legalContent} />;
	if (path === "/status") return <StatusPage />;
	const cardMatch = path.match(/^\/cards\/([^/]+)$/);
	if (cardMatch?.[1])
		return <CardDetailPage id={decodeURIComponent(cardMatch[1])} />;
	const tripTodayMatch = path.match(/^\/trips\/([^/]+)\/today$/);
	if (tripTodayMatch?.[1])
		return <TripTodayPage id={decodeURIComponent(tripTodayMatch[1])} />;
	const tripFilmMatch = path.match(/^\/trips\/([^/]+)\/film$/);
	if (tripFilmMatch?.[1])
		return <TripFilmPage id={decodeURIComponent(tripFilmMatch[1])} />;
	const tripMatch = path.match(/^\/trips\/([^/]+)$/);
	if (tripMatch?.[1])
		return <TripDetailPage id={decodeURIComponent(tripMatch[1])} />;
	const originMatch = path.match(/^\/from\/([a-z]{3})$/i);
	const originCode = originMatch?.[1]?.toUpperCase() as OriginCode | undefined;
	if (originCode && origins.some((item) => item.iata === originCode))
		return <OriginPage origin={originCode} />;
	const routeMatch = path.match(/^\/from\/([a-z]{3})\/to\/([a-z]{3})$/i);
	const routeOrigin = routeMatch?.[1]?.toUpperCase() as OriginCode | undefined;
	const routeDestination = routeMatch?.[2]?.toUpperCase() as
		| AirportCode
		| undefined;
	if (
		routeOrigin &&
		routeDestination &&
		origins.some((item) => item.iata === routeOrigin) &&
		destinationCodes.includes(routeDestination)
	)
		return (
			<RouteDetailPage origin={routeOrigin} destination={routeDestination} />
		);
	const airportMatch = path.match(/^\/airports\/([a-zA-Z]{3})$/);
	const airportCode = airportMatch?.[1]?.toUpperCase() as
		| AirportCode
		| undefined;
	if (airportCode && destinationCodes.includes(airportCode))
		return <EditionPage code={airportCode} />;
	return <NotFoundPage />;
}
import {
	CompanionListPage,
	CompanionEditorPage,
	CompanionDetailPage,
} from "./companions/CompanionPages";
